/**
 * Loads Cloudflare Turnstile, once, for whichever form asks first.
 *
 * WHY THIS FILE EXISTS. Both forms on this site were written against a comment
 * that said "Turnstile renders itself when its script loads" — and nothing in
 * the app ever loaded it. `index.html` has no script tag, no component injected
 * one, so `window.turnstile` was permanently `undefined`, both widgets were
 * permanently empty divs, and every submission went up without a token. The
 * waitlist hid the symptom (`.waitlist-form .cf-turnstile:empty { display:none }`
 * in styles.css) and answers every outcome identically, so it never complained.
 * The contact form has no such cover, and reported the only thing it could see:
 * "the anti-spam check expired".
 *
 * TWO THINGS ARE DELIBERATE HERE.
 *
 * It is not a tag in `index.html`. That template is shared by every route,
 * including the signed-in app, so a tag there loads a third-party script on
 * pages that have no challenge on them. Loading on demand keeps it to the two
 * pages that actually render a widget.
 *
 * It is `render=explicit`. In implicit mode the script scans the DOM for
 * `.cf-turnstile` on load and renders whatever it finds with the attributes it
 * finds there — which in a React tree is a race against the component, and
 * silently ignores the `action` we pin in code. Explicit mode means nothing is
 * rendered until a component asks for it, with the options it passes.
 */

/** Just the surface we use. The real object has more. */
export interface TurnstileApi {
  render(el: HTMLElement, opts: { sitekey: string; action?: string }): string;
  reset(widgetId?: string): void;
  remove(widgetId: string): void;
  getResponse(widgetId?: string): string | undefined;
}

/**
 * Public by design — a sitekey identifies a widget, it does not authorise
 * anything. The secret half never leaves the Worker, and the token this key
 * mints is checked server-side against siteverify, so a copied sitekey buys an
 * attacker a widget on their own page whose tokens our Workers refuse (they
 * pin the hostname siteverify reports).
 */
export const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const CALLBACK = "__siminoTurnstileReady";
const SRC =
  `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${CALLBACK}`;

let pending: Promise<TurnstileApi | null> | null = null;

/**
 * Resolves with the API once the script is up, or with `null` if it can never
 * arrive — no sitekey configured, no DOM (the prerender pass), or the request
 * was blocked. `null` is a normal outcome, not an exception: a privacy
 * extension blocking challenges.cloudflare.com is a real visitor, and the
 * caller's job is to tell them plainly rather than to throw.
 *
 * Memoised, so two forms on one page share a single script element.
 */
export function loadTurnstile(): Promise<TurnstileApi | null> {
  if (pending) return pending;
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null);
  }
  if (!TURNSTILE_SITE_KEY) {
    console.error(
      "VITE_TURNSTILE_SITE_KEY is not set — the challenge cannot render, and " +
      "every submission will be refused by the Worker as `challenge`.",
    );
    return Promise.resolve(null);
  }

  pending = new Promise((resolve) => {
    const already = (window as Window & { turnstile?: TurnstileApi }).turnstile;
    if (already) return resolve(already);

    (window as unknown as Record<string, unknown>)[CALLBACK] = () =>
      resolve((window as Window & { turnstile?: TurnstileApi }).turnstile ?? null);

    const tag = document.createElement("script");
    tag.src = SRC;
    tag.async = true;
    tag.defer = true;
    // Blocked, offline, or the CDN is down. Resolving rather than rejecting
    // keeps the caller on one code path for "there will be no challenge".
    tag.onerror = () => resolve(null);
    document.head.appendChild(tag);
  });

  return pending;
}
